import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { signInWithCustomToken } from "firebase/auth";
import { app, auth } from "./firebaseConfig";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/**
 * Activation landing page for the signup-flow-modernization branch.
 *
 * Flow: user clicks /activate/:token from the activation email →
 * this page calls activateAccount({token}) → on success, signs in with
 * the returned custom token and redirects to /app/dashboard. On error
 * (invalid/expired/etc), shows a clear error state with sign-in fallback.
 *
 * StrictMode-safe via a ref guard so React's double-invoke in dev doesn't
 * fire activateAccount twice.
 */
function Activate() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [phase, setPhase] = useState("loading"); // loading | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const run = async () => {
      if (!token) {
        setPhase("error");
        setErrorMessage(t("activate.errorMissingToken"));
        return;
      }

      try {
        const functions = getFunctions(
          app,
          process.env.REACT_APP_FUNCTIONS_REGION,
        );
        const activate = httpsCallable(functions, "activateAccount");
        const result = await activate({ token });
        const data = result.data || {};

        if (!data.success) {
          setPhase("error");
          setErrorMessage(data.reason || t("activate.errorReasonFallback"));
          return;
        }

        // FREE users get a limited web session and land on the Beta hub
        // (app download links, ArUco templates, FAQ). PRO users (Personal +
        // Team) land in the app dashboard.
        const destination =
          data.role === "free_user"
            ? "/app/beta"
            : "/app/dashboard";

        if (data.customToken) {
          // Auto-sign-in path: server returned a custom token.
          await signInWithCustomToken(auth, data.customToken);
          setPhase("success");
          setTimeout(() => navigate(destination), 1500);
        } else {
          // Account activated but auto-sign-in unavailable (e.g. missing
          // IAM role for createCustomToken). Send user to manual sign-in.
          setPhase("success");
          setTimeout(() => navigate("/login"), 1500);
        }
      } catch (err) {
        console.error("[Activate] activateAccount failed:", err);
        setPhase("error");
        const message = err?.message || t("activate.errorFallback");
        if (message.includes("expired")) {
          setErrorMessage(t("activate.errorExpired"));
        } else if (
          message.includes("not-found") ||
          message.includes("invalid")
        ) {
          setErrorMessage(t("activate.errorInvalid"));
        } else {
          setErrorMessage(message);
        }
      }
    };

    run();
  }, [token, navigate, t]);

  return (
    <Container component="main" maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {phase === "loading" && (
          <>
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ mt: 3 }}>
              {t("activate.loadingTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("activate.loadingSubtitle")}
            </Typography>
          </>
        )}

        {phase === "success" && (
          <>
            <CheckCircleIcon sx={{ fontSize: 48, color: "success.main" }} />
            <Typography variant="h5" component="h1" sx={{ mt: 2 }}>
              {t("activate.successTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("activate.successSubtitle")}
            </Typography>
            <CircularProgress size={24} sx={{ mt: 3 }} />
          </>
        )}

        {phase === "error" && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 48, color: "error.main" }} />
            <Typography variant="h5" component="h1" sx={{ mt: 2 }}>
              {t("activate.errorTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              {errorMessage}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={() => navigate("/")}>
                {t("activate.returnHome")}
              </Button>
              <Button variant="contained" onClick={() => navigate("/login")}>
                {t("activate.goToSignIn")}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
}

export default Activate;
